import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import Modal from './Modal';
import { AuthContext } from '../../context/auth-context';
import {
  getRecord,
  saveRecord,
  clearRecord,
  getSavedAlias,
  saveAlias,
} from './slotMachineRecords';
import {
  fetchLeaderboard,
  submitScore,
  qualifiesForBoard,
} from '../../services/slotLeaderboardService';
import {
  SYMBOLS,
  PAYOUT_TABLE,
  PAYLINES,
  BASE_BET,
  MIN_BET,
  MAX_BET,
  BET_STEP,
  STARTING_CREDITS,
  randomColumn,
  randomGrid,
  cellKey,
  evaluateSpin,
  isLuckyWager,
  luckyGrid,
} from './slotMachineScoring';
import './styling/SlotMachineModal.css';

const REEL_STOP_TIMES = [900, 1550, 2200];

/** Matches MAX_ALIAS_LENGTH in Maestro's slotScore.model.ts */
const MAX_ALIAS_LENGTH = 20;

/**
 * Heap Leach Slots easter egg modal — triggered by 10 rapid clicks on the
 * "Orecasting" title in the project sidebar.
 *
 * Props:
 *   show       {boolean}  — controls modal visibility
 *   onClose    {function} — called when the user dismisses the modal
 */
const SlotMachineModal = ({ show, onClose }) => {
  const [credits, setCredits] = useState(STARTING_CREDITS);
  const [grid, setGrid] = useState(randomGrid);
  const [spinning, setSpinning] = useState([false, false, false]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [message, setMessage] = useState('Pull the lever! 🎰');
  const [lastWin, setLastWin] = useState(0);
  const [totalSpins, setTotalSpins] = useState(0);
  const [activeTab, setActiveTab] = useState('game');
  const [sessionHighScore, setSessionHighScore] = useState(STARTING_CREDITS);

  // Wager: stake per line, and which lines are being played
  const [betPerLine, setBetPerLine] = useState(BASE_BET);
  const [activeLineIds, setActiveLineIds] = useState(['middle']);
  // Cells that took part in a win, for the post-spin highlight
  const [winningCells, setWinningCells] = useState([]);

  // Personal best loaded from localStorage; null until the player sets one
  const [record, setRecord] = useState(null);
  const [storageOk, setStorageOk] = useState(true);

  // Global top-10 board from Maestro
  const [board, setBoard] = useState(null);
  const [boardState, setBoardState] = useState('idle'); // idle | loading | ready | error
  const [aliasInput, setAliasInput] = useState('');
  const [submitState, setSubmitState] = useState('idle'); // idle | sending | done | missed | error
  const [submitError, setSubmitError] = useState('');
  // Highest score already sent this session — beating it can be submitted again
  const [submittedScore, setSubmittedScore] = useState(0);
  const [missedReason, setMissedReason] = useState('');
  // _id of the row this player just submitted, so it can be highlighted
  const [myEntryId, setMyEntryId] = useState(null);

  // Cash-out flow: the board is re-checked on the way out, and a qualifying
  // score is submitted before the modal closes.
  // null | 'checking' | 'prompt' | 'sending' | 'done' | 'missed' | 'error'
  const [cashOutPhase, setCashOutPhase] = useState(null);
  const [cashOutAlias, setCashOutAlias] = useState('');
  const [cashOutRank, setCashOutRank] = useState(null);
  const [cashOutError, setCashOutError] = useState('');

  const intervalsRef = useRef([]);
  const timeoutsRef = useRef([]);
  // Best score as of the moment the modal opened — beating it lights up "NEW RECORD"
  const recordAtOpenRef = useRef(0);
  // After a manual reset, don't re-bank the peak the player had already reached;
  // only a genuinely higher score starts a new record.
  const suppressBelowRef = useRef(0);

  const auth = useContext(AuthContext);
  const playerName =
    auth?.user?.name ||
    auth?.user?.username ||
    (auth?.user?.email ? auth.user.email.split('@')[0] : null) ||
    'You';
  // Key by user id so two people on the same machine keep separate records;
  // fall back to the display name when signed out.
  const playerKey = auth?.UID || auth?.user?._id || `name:${playerName}`;

  // Cleanup all pending timers
  const clearAllTimers = useCallback(() => {
    intervalsRef.current.forEach(clearInterval);
    timeoutsRef.current.forEach(clearTimeout);
    intervalsRef.current = [];
    timeoutsRef.current = [];
  }, []);

  useEffect(() => () => clearAllTimers(), [clearAllTimers]);

  /**
   * Start a fresh run: stake back to the starting credits, counters cleared.
   * The player's wager settings are deliberately left alone — resetting the
   * bankroll shouldn't undo the bet and lines they picked.
   */
  const resetGame = useCallback(() => {
    clearAllTimers();
    setCredits(STARTING_CREDITS);
    setGrid(randomGrid());
    setSpinning([false, false, false]);
    setIsSpinning(false);
    setMessage('Pull the lever! 🎰');
    setLastWin(0);
    setTotalSpins(0);
    setWinningCells([]);
    setSessionHighScore(STARTING_CREDITS);
  }, [clearAllTimers]);

  // Reset state each time the modal opens
  useEffect(() => {
    if (show) {
      resetGame();
      setActiveTab('game');
      setBetPerLine(BASE_BET);
      setActiveLineIds(['middle']);

      // Reload the stored best each open — it may have changed in another tab
      const stored = getRecord(playerKey);
      setRecord(stored);
      recordAtOpenRef.current = stored?.score ?? 0;
      suppressBelowRef.current = 0;
      setStorageOk(true);

      setAliasInput(getSavedAlias(playerKey));
      setSubmitState('idle');
      setSubmitError('');
      setSubmittedScore(0);
      setMissedReason('');
      setMyEntryId(null);
      setCashOutPhase(null);
      setCashOutAlias('');
      setCashOutRank(null);
      setCashOutError('');
    }
  }, [show, resetGame, playerKey]);

  // Load the global board on open. A failure here must not break the game, so
  // it degrades to an error line with the local personal best still shown.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    setBoardState('loading');
    fetchLeaderboard(auth?.token)
      .then(data => {
        if (cancelled) return;
        setBoard(data);
        setBoardState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setBoardState('error');
      });

    return () => { cancelled = true; };
  }, [show, auth?.token]);

  // Track the peak credit balance reached this session
  useEffect(() => {
    setSessionHighScore(prev => (credits > prev ? credits : prev));
  }, [credits]);

  // Persist a new personal best as soon as it happens, so a mid-session refresh
  // or browser close doesn't lose it. Gated on totalSpins so merely opening the
  // modal doesn't bank the starting credits as a "record".
  useEffect(() => {
    if (!show || totalSpins === 0) return;
    // Never bank the starting stake — a record means the player got ahead of it
    const floor = Math.max(record?.score ?? 0, suppressBelowRef.current, STARTING_CREDITS);
    if (sessionHighScore <= floor) return;
    const { record: saved, persisted } = saveRecord(playerKey, {
      name: playerName,
      score: sessionHighScore,
      spins: totalSpins,
    });
    setRecord(saved);
    setStorageOk(persisted);
  }, [show, sessionHighScore, totalSpins, record, playerKey, playerName]);

  /**
   * Send the session best to the board under `alias` and fold the response into
   * local state. Shared by the leaderboard form and the cash-out flow — both
   * must agree on what "submitted" means or the qualify nudge reappears.
   */
  const performSubmit = useCallback(async (alias) => {
    const result = await submitScore(
      { alias, score: sessionHighScore, spins: totalSpins },
      auth?.token,
    );
    setBoard(result);
    setBoardState('ready');
    saveAlias(playerKey, alias);

    let myEntry = null;
    if (result?.qualified) {
      // Identify our row by alias+score — the API doesn't echo which id is ours
      myEntry = (result.leaderboard || []).find(
        entry => entry.alias === alias && entry.score === sessionHighScore,
      ) ?? null;
      setMyEntryId(myEntry?._id ?? null);
      setSubmittedScore(sessionHighScore);
    }
    return { result, myEntry };
  }, [sessionHighScore, totalSpins, auth?.token, playerKey]);

  const handleSubmitScore = useCallback(async (e) => {
    e.preventDefault();
    const alias = aliasInput.trim();
    if (!alias || submitState === 'sending') return;

    setSubmitState('sending');
    setSubmitError('');
    try {
      const { result } = await performSubmit(alias);
      if (result?.qualified) {
        setSubmitState('done');
      } else {
        setMissedReason(result?.reason || '');
        setSubmitState('missed');
      }
    } catch (err) {
      setSubmitState('error');
      setSubmitError(err?.message || 'Could not submit your score.');
    }
  }, [aliasInput, submitState, performSubmit]);

  const submitCashOutScore = useCallback(async (alias) => {
    setCashOutAlias(alias);
    setCashOutPhase('sending');
    setCashOutError('');
    try {
      const { result, myEntry } = await performSubmit(alias);
      if (result?.qualified) {
        setCashOutRank(myEntry?.rank ?? null);
        setCashOutPhase('done');
      } else {
        setMissedReason(result?.reason || '');
        setCashOutPhase('missed');
      }
    } catch (err) {
      setCashOutError(err?.message || 'Could not submit your score.');
      setCashOutPhase('error');
    }
  }, [performSubmit]);

  const handleClearRecord = useCallback(() => {
    clearRecord(playerKey);
    setRecord(null);
    recordAtOpenRef.current = 0;
    suppressBelowRef.current = sessionHighScore;
  }, [playerKey, sessionHighScore]);

  /**
   * Scores every active payline against the settled grid. Lines pay
   * independently and stack, so one spin can hit several at once.
   */
  const evaluateResult = useCallback((finalGrid, lineIds, stake) => {
    const { total, hits, best, cells } = evaluateSpin(finalGrid, lineIds, stake);

    if (hits.length === 0) {
      setLastWin(0);
      setWinningCells([]);
      setMessage('No match — try again!');
      return;
    }

    // Headline the biggest hit; note the rest as a count so the line stays short
    const extra = hits.length > 1 ? ` (${hits.length} lines)` : ` on ${best.lineLabel}`;

    setCredits(prev => prev + total);
    setLastWin(total);
    setWinningCells(cells);
    setMessage(`${best.label} +${total} credits${extra}!`);
  }, []);

  const totalBet = betPerLine * activeLineIds.length;

  const spin = useCallback(() => {
    if (isSpinning || activeLineIds.length === 0 || credits < totalBet) return;

    // Snapshot the wager so mid-spin control changes can't rescore this spin
    const stake = betPerLine;
    const lineIds = [...activeLineIds];

    clearAllTimers();
    setActiveTab('game');
    setCredits(prev => prev - totalBet);
    setTotalSpins(prev => prev + 1);
    setIsSpinning(true);
    setLastWin(0);
    setWinningCells([]);
    setMessage('Spinning…');
    setSpinning([true, true, true]);

    // Pre-determine the final outcome; a lucky wager rigs the top row
    const finalGrid = isLuckyWager(stake, lineIds) ? luckyGrid() : randomGrid();

    // Rapidly cycle each column's symbols during the spin
    const intervals = finalGrid.map((_, col) =>
      setInterval(() => {
        setGrid(prev => {
          const next = [...prev];
          next[col] = randomColumn();
          return next;
        });
      }, 75)
    );
    intervalsRef.current = intervals;

    // Stop each column in turn, then evaluate on the last stop
    REEL_STOP_TIMES.forEach((delay, col) => {
      const t = setTimeout(() => {
        clearInterval(intervals[col]);
        setGrid(prev => {
          const next = [...prev];
          next[col] = finalGrid[col];
          return next;
        });
        setSpinning(prev => {
          const next = [...prev];
          next[col] = false;
          return next;
        });

        if (col === REEL_STOP_TIMES.length - 1) {
          setIsSpinning(false);
          evaluateResult(finalGrid, lineIds, stake);
        }
      }, delay);
      timeoutsRef.current.push(t);
    });
  }, [
    isSpinning,
    credits,
    totalBet,
    betPerLine,
    activeLineIds,
    clearAllTimers,
    evaluateResult,
  ]);

  const adjustBet = useCallback((delta) => {
    if (isSpinning) return;
    setBetPerLine(prev =>
      Math.min(MAX_BET, Math.max(MIN_BET, prev + delta * BET_STEP)),
    );
  }, [isSpinning]);

  const toggleLine = useCallback((lineId) => {
    if (isSpinning) return;
    setActiveLineIds(prev => {
      if (!prev.includes(lineId)) return [...prev, lineId];
      // Never leave the player with nothing to play
      if (prev.length === 1) return prev;
      return prev.filter(id => id !== lineId);
    });
  }, [isSpinning]);

  const handleResetGame = useCallback(() => {
    resetGame();
    setSubmitState('idle');
    setMissedReason('');
    // A fresh run measures "new record" against the stored best again
    recordAtOpenRef.current = record?.score ?? 0;
  }, [resetGame, record]);

  const handleClose = useCallback(() => {
    clearAllTimers();
    setIsSpinning(false);
    setSpinning([false, false, false]);
    onClose();
  }, [clearAllTimers, onClose]);

  /**
   * Cash Out: before closing, re-check the live board (the cutoff may have
   * moved while they played) and bank a qualifying session best. A player with
   * a saved alias is submitted automatically; anyone else gets one prompt to
   * pick a name. Every failure path degrades to a plain close — the door out
   * must never be blocked by a dead endpoint.
   */
  const handleCashOut = useCallback(async () => {
    clearAllTimers();
    setIsSpinning(false);
    setSpinning([false, false, false]);

    const best = sessionHighScore;
    const hasFreshBest =
      totalSpins > 0 && best > STARTING_CREDITS && best > submittedScore;
    if (!hasFreshBest) {
      onClose();
      return;
    }

    setCashOutPhase('checking');
    let latest = board;
    try {
      latest = await fetchLeaderboard(auth?.token);
      setBoard(latest);
      setBoardState('ready');
    } catch {
      // Board unreachable — fall back to the copy loaded on open, if any
      if (!latest) {
        onClose();
        return;
      }
    }

    // An already-placed row may always be improved, cutoff or not
    if (!qualifiesForBoard(best, latest) && myEntryId === null) {
      onClose();
      return;
    }

    const savedAlias = getSavedAlias(playerKey).trim();
    if (savedAlias) {
      await submitCashOutScore(savedAlias);
    } else {
      setCashOutPhase('prompt');
    }
  }, [
    clearAllTimers,
    sessionHighScore,
    totalSpins,
    submittedScore,
    onClose,
    board,
    auth?.token,
    myEntryId,
    playerKey,
    submitCashOutScore,
  ]);

  const handleCashOutPromptSubmit = useCallback((e) => {
    e.preventDefault();
    const alias = aliasInput.trim();
    if (!alias) return;
    submitCashOutScore(alias);
  }, [aliasInput, submitCashOutScore]);

  // The player's row is the better of their stored record and the live session,
  // so an in-progress hot streak shows up before it is banked.
  const liveBeatsRecord = !record || sessionHighScore > record.score;
  const personalBest = liveBeatsRecord
    ? { score: sessionHighScore, spins: totalSpins, updatedAt: null }
    : { score: record.score, spins: record.spins, updatedAt: record.updatedAt };

  const isNewRecord =
    totalSpins > 0 &&
    sessionHighScore > Math.max(recordAtOpenRef.current, STARTING_CREDITS);

  const boardEntries = board?.leaderboard ?? [];
  // Offer submission only for a score that actually clears the current bar and
  // that the player hasn't already banked this session.
  const canSubmitScore =
    boardState === 'ready' &&
    totalSpins > 0 &&
    sessionHighScore > STARTING_CREDITS &&
    sessionHighScore > submittedScore &&
    (qualifiesForBoard(sessionHighScore, board) || myEntryId !== null);

  const isJackpot = lastWin >= 200;
  // Can't cover this spin — either genuinely broke, or just over-betting.
  // Distinguishing them matters: at 5 lines × 50 the max bet is 250, well over
  // the 100 starting credits, and "Out of Credits" would be a lie.
  const cannotAfford = credits < totalBet && !isSpinning;
  const isBroke = credits < MIN_BET && !isSpinning;

  const spinButtonLabel = isSpinning
    ? 'Spinning…'
    : isBroke
    ? '🪙 Out of Credits'
    : cannotAfford
    ? '⬇ Lower your bet'
    : `SPIN  (${totalBet} credits)`;

  return (
    <Modal
      show={show}
      onCancel={handleClose}
      header="🎰 Heap Leach Slots"
      className={`slot-machine-modal${isJackpot ? ' jackpot-glow' : ''}`}
      headerClass=""
      contentClass="slot-machine-content"
      footerClass="slot-machine-footer"
      footer={
        <div className="slot-footer-inner">
          <span className="slot-spins-counter">Spins: {totalSpins}</span>
          <button
            className={`slot-spin-btn${isSpinning ? ' spinning' : ''}${cannotAfford ? ' disabled' : ''}`}
            onClick={spin}
            disabled={isSpinning || cannotAfford || cashOutPhase !== null}
          >
            {spinButtonLabel}
          </button>
          <button
            className={`slot-reset-btn${isBroke ? ' urgent' : ''}`}
            onClick={handleResetGame}
            disabled={isSpinning || cashOutPhase !== null}
            title="Start a fresh run without closing the game"
          >
            ↻ New Run
          </button>
          <button
            className="slot-close-btn"
            onClick={cashOutPhase === null ? handleCashOut : handleClose}
          >
            {cashOutPhase === null ? 'Cash Out' : 'Close'}
          </button>
        </div>
      }
    >
      {/* Tab bar — hidden while the cash-out flow has the floor */}
      {cashOutPhase === null && (
      <div className="slot-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'game'}
          className={`slot-tab${activeTab === 'game' ? ' active' : ''}`}
          onClick={() => setActiveTab('game')}
        >
          🎰 Play
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'leaderboard'}
          className={`slot-tab${activeTab === 'leaderboard' ? ' active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          🏆 Leaderboard
        </button>
      </div>
      )}

      {cashOutPhase !== null ? (
        /* Cash-out flow — check, submit, or prompt for a name on the way out */
        <div className="slot-cashout">
          {cashOutPhase === 'checking' && (
            <p className="slot-cashout-status">Checking the leaderboard…</p>
          )}

          {cashOutPhase === 'prompt' && (
            <>
              <p className="slot-cashout-headline">
                🏆 Your best — {sessionHighScore.toLocaleString()} cr — makes the
                global top {board?.size ?? 10}!
              </p>
              <form className="leaderboard-submit" onSubmit={handleCashOutPromptSubmit}>
                <p className="leaderboard-submit-label">Pick a name before you go:</p>
                <div className="leaderboard-submit-row">
                  <input
                    className="leaderboard-alias-input"
                    type="text"
                    value={aliasInput}
                    onChange={e => setAliasInput(e.target.value)}
                    maxLength={MAX_ALIAS_LENGTH}
                    placeholder="Name for the board"
                    aria-label="Name to show on the leaderboard"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="leaderboard-submit-btn"
                    disabled={!aliasInput.trim()}
                  >
                    Submit
                  </button>
                </div>
                <p className="leaderboard-hint">
                  Only this name is stored — never your account, email, or company.
                </p>
              </form>
              <div className="slot-cashout-actions">
                <button
                  type="button"
                  className="slot-cashout-secondary-btn"
                  onClick={() => setCashOutPhase(null)}
                >
                  Keep playing
                </button>
                <button
                  type="button"
                  className="slot-cashout-secondary-btn"
                  onClick={handleClose}
                >
                  Skip &amp; cash out
                </button>
              </div>
            </>
          )}

          {cashOutPhase === 'sending' && (
            <p className="slot-cashout-status">
              Submitting {sessionHighScore.toLocaleString()} cr as “{cashOutAlias}”…
            </p>
          )}

          {cashOutPhase === 'done' && (
            <>
              <p className="slot-cashout-headline">
                🎉 “{cashOutAlias}” is {cashOutRank ? `#${cashOutRank}` : ''} on the
                global board with {sessionHighScore.toLocaleString()} cr!
              </p>
              <div className="slot-cashout-actions">
                <button
                  type="button"
                  className="leaderboard-submit-btn"
                  onClick={handleClose}
                >
                  Done
                </button>
              </div>
            </>
          )}

          {cashOutPhase === 'missed' && (
            <>
              <p className="slot-cashout-status">
                {missedReason === 'existing_higher'
                  ? `“${cashOutAlias}” already holds a better score on the board — nothing to update.`
                  : 'Someone claimed the last slot first — the bar moved while you played.'}
              </p>
              <div className="slot-cashout-actions">
                <button
                  type="button"
                  className="leaderboard-submit-btn"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </>
          )}

          {cashOutPhase === 'error' && (
            <>
              <p className="slot-cashout-status slot-cashout-status--error">
                {cashOutError}
              </p>
              <div className="slot-cashout-actions">
                <button
                  type="button"
                  className="leaderboard-submit-btn"
                  onClick={() => submitCashOutScore(cashOutAlias || aliasInput.trim())}
                >
                  Try again
                </button>
                <button
                  type="button"
                  className="slot-cashout-secondary-btn"
                  onClick={handleClose}
                >
                  Close anyway
                </button>
              </div>
            </>
          )}
        </div>
      ) : activeTab === 'game' ? (
        <>
          {/* Credits display */}
          <div className="slot-credits-bar">
            <div className="slot-credits-block">
              <span className="slot-credits-label">CREDITS</span>
              <span className={`slot-credits-value${lastWin > 0 ? ' win-flash' : ''}`}>
                {credits}
              </span>
            </div>
            {lastWin > 0 && (
              <div className="slot-last-win-block">
                <span className="slot-credits-label">LAST WIN</span>
                <span className="slot-last-win-value">+{lastWin}</span>
              </div>
            )}
          </div>

          {/* Main machine frame */}
          <div className="slot-machine-frame">
            <div className="slot-machine-top-light">
              <div className={`slot-top-bulb${isJackpot ? ' jackpot-flash' : ''}`} />
              <div className={`slot-top-bulb${isJackpot ? ' jackpot-flash' : ''}`} />
              <div className={`slot-top-bulb${isJackpot ? ' jackpot-flash' : ''}`} />
              <div className={`slot-top-bulb${isJackpot ? ' jackpot-flash' : ''}`} />
              <div className={`slot-top-bulb${isJackpot ? ' jackpot-flash' : ''}`} />
            </div>

            <div className="slot-reels-wrapper">
              <div className="slot-grid">
                {grid.map((column, col) => (
                  <div
                    key={col}
                    className={`slot-reel${spinning[col] ? ' reel-spinning' : ''}`}
                  >
                    {column.map((symbol, row) => (
                      <div
                        key={row}
                        className={`slot-cell${
                          winningCells.includes(cellKey(col, row)) ? ' cell-win' : ''
                        }`}
                      >
                        <span className="slot-emoji" role="img" aria-label={symbol.label}>
                          {symbol.emoji}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Wager controls */}
          <div className="slot-controls">
            <div className="slot-bet">
              <span className="slot-control-label">Bet / line</span>
              <div className="slot-bet-stepper">
                <button
                  type="button"
                  className="slot-step-btn"
                  onClick={() => adjustBet(-1)}
                  disabled={isSpinning || betPerLine <= MIN_BET}
                  aria-label="Decrease bet per line"
                >
                  −
                </button>
                <span className="slot-bet-value">{betPerLine}</span>
                <button
                  type="button"
                  className="slot-step-btn"
                  onClick={() => adjustBet(1)}
                  disabled={isSpinning || betPerLine >= MAX_BET}
                  aria-label="Increase bet per line"
                >
                  +
                </button>
              </div>
            </div>

            <div className="slot-lines">
              <span className="slot-control-label">
                Lines ({activeLineIds.length}/{PAYLINES.length})
              </span>
              <div className="slot-line-buttons">
                {PAYLINES.map(line => {
                  const active = activeLineIds.includes(line.id);
                  return (
                    <button
                      key={line.id}
                      type="button"
                      className={`slot-line-btn${active ? ' active' : ''}`}
                      onClick={() => toggleLine(line.id)}
                      disabled={isSpinning}
                      aria-pressed={active}
                    >
                      {line.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="slot-total-bet">
              <span className="slot-control-label">Total bet</span>
              <span className="slot-total-bet-value">{totalBet}</span>
            </div>
          </div>

          {/* Result message */}
          <div
            className={`slot-message${lastWin > 0 ? ' slot-win' : ''}${isJackpot ? ' jackpot' : ''}`}
            aria-live="polite"
          >
            {message}
          </div>

          {canSubmitScore && (
            <button
              type="button"
              className="slot-qualify-nudge"
              onClick={() => setActiveTab('leaderboard')}
            >
              🏆 {sessionHighScore.toLocaleString()} cr makes the global top{' '}
              {board?.size ?? 10} — claim it
            </button>
          )}

          {/* Pay table */}
          <details className="slot-payout-table">
            <summary className="payout-summary">
              Pay Table (per line, at {betPerLine}/line)
            </summary>
            <div className="payout-rows">
              {PAYOUT_TABLE.map(p => {
                const sym = SYMBOLS.find(s => s.id === p.symbolId);
                const scale = betPerLine / BASE_BET;
                return (
                  <div key={p.symbolId} className="payout-row">
                    <span className="payout-symbols">
                      {sym.emoji}{sym.emoji}{sym.emoji}
                    </span>
                    <span className="payout-triple">{Math.round(p.triple * scale)} cr</span>
                    <span className="payout-pair">
                      pair: {Math.round(p.pair * scale)} cr
                    </span>
                  </div>
                );
              })}
            </div>
          </details>
        </>
      ) : (
        /* Leaderboard view */
        <div className="slot-leaderboard">
          <p className="leaderboard-subtitle">
            Global top {board?.size ?? 10} — every company, names only
          </p>

          {boardState === 'loading' && (
            <div className="leaderboard-status">Loading the board…</div>
          )}

          {boardState === 'error' && (
            <div className="leaderboard-status leaderboard-status--error">
              Couldn&apos;t reach the leaderboard. Your personal best below is safe.
            </div>
          )}

          {boardState === 'ready' && boardEntries.length === 0 && (
            <div className="leaderboard-status">No scores yet — be the first.</div>
          )}

          {boardState === 'ready' && boardEntries.length > 0 && (
            <div className="leaderboard-list" role="list">
              {boardEntries.map((entry, i) => (
                <div
                  key={entry._id}
                  role="listitem"
                  className={`leaderboard-row${entry._id === myEntryId ? ' you' : ''}${i === 0 ? ' top' : ''}`}
                >
                  <span className="lb-rank">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${entry.rank}`}
                  </span>
                  <span className="lb-name">
                    {entry.alias}
                    {entry._id === myEntryId && <span className="lb-you-badge">YOU</span>}
                  </span>
                  <span
                    className="lb-score"
                    title={
                      entry.createdAt
                        ? `Set ${new Date(entry.createdAt).toLocaleDateString()}`
                        : undefined
                    }
                  >
                    {entry.score.toLocaleString()} cr
                  </span>
                  <span className="lb-spins">{entry.spins} spins</span>
                </div>
              ))}
            </div>
          )}

          {/* Submission — only when this run actually clears the bar */}
          {canSubmitScore && (
            <form className="leaderboard-submit" onSubmit={handleSubmitScore}>
              <p className="leaderboard-submit-label">
                🏆 {sessionHighScore.toLocaleString()} cr makes the board! Pick a name:
              </p>
              <div className="leaderboard-submit-row">
                <input
                  className="leaderboard-alias-input"
                  type="text"
                  value={aliasInput}
                  onChange={e => setAliasInput(e.target.value)}
                  maxLength={MAX_ALIAS_LENGTH}
                  placeholder="Name for the board"
                  aria-label="Name to show on the leaderboard"
                />
                <button
                  type="submit"
                  className="leaderboard-submit-btn"
                  disabled={submitState === 'sending' || !aliasInput.trim()}
                >
                  {submitState === 'sending' ? 'Submitting…' : 'Submit'}
                </button>
              </div>
              <p className="leaderboard-hint">
                Only this name is stored — never your account, email, or company.
              </p>
            </form>
          )}

          {submitState === 'done' && (
            <div className="leaderboard-status">You&apos;re on the board 🎉</div>
          )}
          {submitState === 'missed' && (
            <div className="leaderboard-status">
              {missedReason === 'existing_higher'
                ? 'That name already holds a better score on the board.'
                : 'Someone claimed the last slot first — the bar moved while you played.'}
            </div>
          )}
          {submitState === 'error' && (
            <div className="leaderboard-status leaderboard-status--error">{submitError}</div>
          )}

          {/* Device-local personal best, independent of the global board */}
          <div className="leaderboard-personal">
            <span className="lb-name">
              Your best on this device
              {isNewRecord && <span className="lb-record-badge">NEW RECORD</span>}
            </span>
            <span
              className="lb-score"
              title={
                personalBest.updatedAt
                  ? `Set ${new Date(personalBest.updatedAt).toLocaleDateString()}`
                  : undefined
              }
            >
              {personalBest.score.toLocaleString()} cr
            </span>
            <span className="lb-spins">{personalBest.spins} spins</span>
          </div>

          <div className="leaderboard-footer">
            <p className="leaderboard-note">
              {storageOk
                ? '🏆 Your personal best is saved on this device only'
                : "⚠️ This browser won't let us save — your record lasts until you close the game"}
            </p>
            {record && (
              <button
                type="button"
                className="leaderboard-reset-btn"
                onClick={handleClearRecord}
              >
                Reset my record
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SlotMachineModal;
